<template>
<div class="subsidiary-feedback-root">
  <!-- 可滚动内容区：与 #app overflow:hidden 配合，必须在内部滚动 -->
  <div class="subsidiary-feedback-scroll">
    <!-- 加载页 -->
    <div v-if="loading" class="loading-wrap">加载中...</div>

  <template v-if="!loading && orderData.vbeln">
    <!-- 顶部卡片：订单信息 -->
    <div class="card">
      <div class="card-body">
        <div class="order-header">
          <div class="order-header-left">
            <div class="order-header-top">
              <span class="order-po-tag">PO</span>
              <span class="order-number">{{ orderData.customerpono || '' }}</span>
            </div>
            <div class="order-header-bottom">
              <span class="order-link-no">{{ orderData.bstkd || '' }}</span>
            </div>
          </div>
          <div class="order-header-right">
            <span class="order-overdue">逾{{ orderData.overdueDay || 0 }}天</span>
            <span class="order-salesman">业务 <span class="highlight">{{ orderData.ywymc || '' }}</span></span>
          </div>
        </div>
        <div class="order-meta-tags">
          <span class="order-meta-tag">{{ orderData.zproject1 || '' }}</span>
          <span class="order-meta-tag">{{ orderData.zmodel || '' }}</span>
        </div>
        <div class="order-amount-row">
          <span class="info-label">订单金额</span>
          <span class="info-value orange"><span class="currency">&yen;</span>{{ formatAmount(orderData.dmbtr) }}</span>
        </div>
        <div class="order-amount-row">
          <span class="info-label">售达方</span>
          <span class="info-value">{{ orderData.kunnrT || '' }}</span>
        </div>

        <!-- 物料明细：数据与订单同期加载，此处仅展开/收起 -->
        <div class="material-fold-bar" @click.stop="toggleMaterial">
          <span class="material-fold-text">物料明细</span>
          <span class="material-fold-arrow" :class="{ expanded: materialExpanded }">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
          </span>
        </div>
        <div class="material-fold-body" v-show="materialExpanded">
          <table class="material-table" v-if="materialList.length > 0">
            <thead>
              <tr>
                <th style="width:40px;">序号</th>
                <th>物料名称</th>
                <th style="width:50px;">数量</th>
                <th style="width:90px;">金额</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in materialList" :key="idx">
                <td>{{ idx + 1 }}</td>
                <td>{{ item.materialName || item.maktx || '' }}</td>
                <td class="text-center">{{ item.num || item.kwmeng || 0 }}</td>
                <td class="text-right">{{ formatAmount(item.taxMoney || item.netwr) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="empty-material">暂无物料数据</div>
        </div>
      </div>
    </div>

    <!-- 中间卡片：反馈表单 -->
    <div class="card">
      <div class="card-body form-card">
        <!-- 预计开票/回款日期 -->
        <div class="form-group" v-if="pageType == 1">
          <div class="form-label">预计开票日期 <span class="required">*</span></div>
          <div class="date-input-wrapper" @click="showNativeDatePicker('vatDateNative')">
            <input
              :key="'predictVatDat-display-' + formKey"
              type="text"
              class="form-date-display"
              :value="formatDateDisplay(changeForm.predictVatDat)"
              placeholder="年 / 月 / 日"
              readonly
            >
            <input
              ref="vatDateNative"
              type="date"
              class="date-native-overlay"
              :value="changeForm.predictVatDat || ''"
              @change="handleNativeDateChange($event, 'predictVatDat')"
            >
          </div>
        </div>
        <div class="form-group" v-if="pageType == 2">
          <div class="form-label">预计回款日期</div>
          <div class="date-input-wrapper" @click="showNativeDatePicker('backDateNative')">
            <input
              :key="'predictBackDat-display-' + formKey"
              type="text"
              class="form-date-display"
              :value="formatDateDisplay(changeForm.predictBackDat)"
              placeholder="年 / 月 / 日"
              readonly
            >
            <input
              ref="backDateNative"
              type="date"
              class="date-native-overlay"
              :value="changeForm.predictBackDat || ''"
              @change="handleNativeDateChange($event, 'predictBackDat')"
            >
          </div>
        </div>

        <!-- 未开票/未回款原因 -->
        <div class="form-group" v-if="pageType == 1">
          <div class="form-label">未开票原因 <span class="required">*</span></div>
          <div class="enum-group" :key="'vatGroup-' + formKey">
            <div
              class="enum-option"
              :class="{ selected: changeForm.vatOverdueDesc === opt }"
              v-for="opt in vatReasons"
              :key="opt"
              @click="changeForm.vatOverdueDesc = opt"
            >
              <div class="check-icon" v-show="changeForm.vatOverdueDesc === opt">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <span>{{ opt }}</span>
            </div>
          </div>
        </div>
        <div class="form-group" v-if="pageType == 2">
          <div class="form-label">未回款原因 <span class="required">*</span></div>
          <div class="enum-group" :key="'backGroup-' + formKey">
            <div
              class="enum-option"
              :class="{ selected: changeForm.backOverdueDesc === opt }"
              v-for="opt in backReasons"
              :key="opt"
              @click="changeForm.backOverdueDesc = opt"
            >
              <div class="check-icon" v-show="changeForm.backOverdueDesc === opt">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <span>{{ opt }}</span>
            </div>
          </div>
        </div>

        <!-- 重复订单（客户取消时显示） -->
        <div class="form-group" v-if="showRepeatOrderFields">
          <div class="form-label">重复订单 <span class="required">*</span></div>
          <el-input
            :key="'repeatOrder-' + formKey"
            v-model="changeForm.repeatOrderNumber"
            class="form-control-field"
            placeholder="若有重复 link 单，则填写 link；无则不填"
          ></el-input>
        </div>

        <!-- 是否有客户证明 -->
        <div class="form-group" v-if="showRepeatOrderFields">
          <div class="form-label">是否有客户证明 <span class="required">*</span></div>
          <div class="enum-group" :key="'proofGroup-' + formKey">
            <div class="enum-option" :class="{ selected: changeForm.hasCustomerProof === 1 }" @click="changeForm.hasCustomerProof = 1">
              <div class="check-icon" v-show="changeForm.hasCustomerProof === 1">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <span>是</span>
            </div>
            <div class="enum-option" :class="{ selected: changeForm.hasCustomerProof === 0 }" @click="changeForm.hasCustomerProof = 0">
              <div class="check-icon" v-show="changeForm.hasCustomerProof === 0">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <span>无</span>
            </div>
          </div>
        </div>

        <!-- 上传证明 -->
        <div class="form-group" v-if="showRepeatOrderFields && changeForm.hasCustomerProof === 1">
          <div class="form-label">上传证明截图 <span class="required">*</span></div>
          <div class="upload-area" @click="$refs.fileInput.click()">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <span>点击上传截图</span>
            <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleFileSelect">
          </div>
          <div class="upload-preview" v-if="changeForm.imageUrls.length > 0">
            <div class="preview-item" v-for="(url, idx) in changeForm.imageUrls" :key="idx">
              <img :src="url" alt="预览">
              <span class="upload-remove" @click="removeImage(idx)">删除</span>
            </div>
          </div>
        </div>

        <!-- 补充说明 -->
        <div class="form-group">
          <div class="form-label">补充说明 <span class="required">*</span></div>
          <el-input
            :key="'feedbackRemark-' + formKey"
            v-model="changeForm.feedbackRemark"
            type="textarea"
            :rows="6"
            class="form-remark-field"
            placeholder="如有其他需要说明的情况，请在此填写"
          ></el-input>
        </div>
      </div>
    </div>

    <!-- 底部卡片：反馈历史 -->
    <div class="card">
      <div class="card-header">
        <div class="icon-box">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 3a9 9 0 00-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0013 21a9 9 0 000-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>
        </div>
        反馈历史
      </div>
      <div class="card-body">
        <div class="history-item" v-if="feedbackHistory.length === 0">
          <div class="no-history">暂无反馈记录</div>
        </div>
        <div class="history-item" v-for="(item, idx) in feedbackHistory" :key="idx">
          <div class="history-header">
            <span class="history-time">{{ item.feedbackTime | formatDay }}</span>
            <span class="history-user">{{ item.creator || item.zhxgrmc || '' }}</span>
          </div>
          <div class="history-body">
            <!-- 按 dataty 展示两行：原因 + 预计日期（与快照字段 overdueDesc/predictDat 及旧字段兼容） -->
            <div class="history-row" v-if="historySnapshotReasonText(item)">
              <span class="h-label h-label-wide">{{ historyReasonLabel(item) }}</span>
              <span class="h-value">{{ historySnapshotReasonText(item) }}</span>
            </div>
            <div class="history-row" v-if="historySnapshotDateRaw(item)">
              <span class="h-label h-label-wide">{{ historyDateLabel(item) }}</span>
              <span class="h-value">{{ historySnapshotDateRaw(item) | formatDay }}</span>
            </div>
            <div class="history-row" v-if="item.feedbackRemark">
              <span class="h-label">补充说明</span>
              <span class="h-value">{{ item.feedbackRemark }}</span>
            </div>
            <div class="history-row" v-if="item.imageUrl">
              <span class="h-label">图片</span>
              <span class="h-value"><img :src="item.imageUrl" class="history-img" @click="previewImage(item.imageUrl)"></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

  <el-empty v-if="!loading && !orderData.vbeln" description="暂无数据"></el-empty>

  </div>

  <!-- 底部操作栏：随 flex 贴在视口底部，避免 fixed 遮挡滚动末尾与安全区 -->
  <div class="bottom-action-bar" v-if="orderData.vbeln">
    <el-button size="mini" class="action-btn-secondary" v-if="isLeaderOrSession" @click="urgeFollowUp">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M22 17H2a3 3 0 003 3h14a3 3 0 003-3z"/><path d="M18 8V6a6 6 0 00-12 0v2H4v9h16V8h-2zM9 8V6a3 3 0 016 0v2"/></svg>
      催业务员跟进
    </el-button>
    <el-button size="mini" class="action-btn-primary" @click="submitFeedback">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><polyline points="20 6 9 17 4 12"/></svg>
      提交反馈
    </el-button>
  </div>
</div>
</template>

<script>
import util from '@/common/util';
import h5LoginMixin from './loginMixin';

export default {
  name: 'SubsidiaryFeedback',
  mixins: [h5LoginMixin],
  data() {
    return {
      loading: true,
      pageType: 1, // 1=已送货未开票, 2=已开票未回款
      isLeader: false, // 是否子公司负责人
      principalId: '',
      vbeln: '',
      orderData: {},
      formKey: 0,
      changeForm: {
        vbeln: '',
        vatOverdueDesc: '',
        backOverdueDesc: '',
        predictVatDat: '',
        predictBackDat: '',
        firstFollower: '',
        creator: '',
        email: '',
        feedbackRemark: '',
        imageUrls: [],
        repeatOrderNumber: '',
        hasCustomerProof: '',
      },
      materialList: [],
      materialExpanded: false,
      feedbackHistory: [],
      vatReasons: ['未完全收货', '客户取消', '退货未核销', '符合开票条件', '客户不开票'],
      backReasons: ['发票未送达', '客户无预算', '发票丢失', '客户已回款', '已收票未挂账', '客户取消'],
    };
  },
  computed: {
    showRepeatOrderFields() {
      return this.changeForm.vatOverdueDesc === '客户取消' || this.changeForm.backOverdueDesc === '客户取消';
    },
    isLeaderOrSession() {
      return this.isLeader || sessionStorage.getItem('IS_SUBSIDIARY_LEADER') === 'true';
    },
  },
  filters: {
    formatDay(time) {
      return util.formatDate(time);
    },
  },
  created() {
    this.vbeln = this.$route.query.vbeln || '';
    this.pageType = this.$route.query.type || '1';
    this.isLeader = this.$route.query.isLeader === 'true';
    document.title = '应收逾期反馈';
  },
  methods: {
    onLoginSuccess() {
      this.principalId = this.$route.query.principalId || sessionStorage.getItem('UNAME') || '';
      // 确保 vbeln 已设置（mixin created 可能先于组件 created 执行）
      if (!this.vbeln) {
        this.vbeln = this.$route.query.vbeln || '';
      }
      if (this.vbeln) {
        // bootstrapFeedbackData：首屏并行拉订单、跟进记录、物料明细（全部 isHideLoading，避免全局 Loading 单例遮罩问题）
        this.bootstrapFeedbackData();
      } else {
        this.loading = false;
      }
    },
    // 首批数据：并行请求且不启全局 Loading（本页有 loading 占位；拦截器仅用单例会并发错位导致透明遮罩挡交互）
    async bootstrapFeedbackData() {
      try {
        await Promise.all([
          this.fetchOrderDetail(),
          this.fetchFeedbackHistory(),
          this.fetchMaterialList(),
        ]);
      } catch (e) {
        this.$message.error('数据加载异常');
        this.feedbackHistory = [];
      } finally {
        this.loading = false;
      }
    },
    // 拉订单详情（由 bootstrapFeedbackData / 页面编排调用）
    async fetchOrderDetail() {
      let res = await this.$api.receivableQyweixinDetails({
        type: this.pageType,
        vbeln: this.vbeln,
        queryStr: '',
        pageNum: 1,
        pageSize: 15,
      }, { isHideLoading: true });
      if (res.code === 200 && res.result && res.result.list && res.result.list.length > 0) {
        this.orderData = res.result.list[0];
      }

      this.changeForm.vbeln = this.vbeln;
      this.changeForm.firstFollower = sessionStorage.getItem('NICKNAME') || '';
      this.changeForm.creator = sessionStorage.getItem('NICKNAME') || '';

      // 标记已读（不传全局 Loading）
      if (this.isLeader && this.vbeln) {
        this.$api.receivableMarkRead({ vbeln: this.vbeln }, { isHideLoading: true }).catch(() => {});
      }
    },
    // 拉反馈历史（由 bootstrapFeedbackData / 提交成功后调用）
    async fetchFeedbackHistory() {
      let res = await this.$api.feedbackRecord({ vbeln: this.vbeln }, { isHideLoading: true });
      if (res.code === 200 && res.result) {
        this.feedbackHistory = res.result;
      }
    },
    /**
     * 反馈历史：与 item.dataty 匹配的「原因」列标题（无 dataty 时根据旧字段推断）。
     */
    historyReasonLabel(item) {
      const d = item && item.dataty;
      if (d === '已开票未回款') return '未回款原因';
      if (d === '已送货未开票') return '未开票原因';
      if (d === '已完结') return '原因';
      if (item && item.backOverdueDesc && !item.vatOverdueDesc) return '未回款原因';
      if (item && item.vatOverdueDesc && !item.backOverdueDesc) return '未开票原因';
      return '原因';
    },
    /**
     * 反馈历史：与 item.dataty 匹配的「预计日期」列标题。
     */
    historyDateLabel(item) {
      const d = item && item.dataty;
      if (d === '已开票未回款') return '预计回款日期';
      if (d === '已送货未开票') return '预计开票日期';
      if (d === '已完结') return '预计日期';
      if (item && item.backOverdueDesc && !item.vatOverdueDesc) return '预计回款日期';
      if (item && item.vatOverdueDesc && !item.backOverdueDesc) return '预计开票日期';
      return '预计日期';
    },
    /**
     * 反馈历史：原因展示文案（优先快照 overdueDesc，否则兼容 vat/back 旧字段）。
     */
    historySnapshotReasonText(item) {
      if (!item) return '';
      if (item.overdueDesc) return item.overdueDesc;
      if (item.dataty === '已开票未回款' && item.backOverdueDesc) return item.backOverdueDesc;
      if (item.dataty === '已送货未开票' && item.vatOverdueDesc) return item.vatOverdueDesc;
      if (item.backOverdueDesc) return item.backOverdueDesc;
      if (item.vatOverdueDesc) return item.vatOverdueDesc;
      return '';
    },
    /**
     * 反馈历史：预计日期原始值（优先 predictDat，否则兼容 predictVatDat/predictBackDat）。
     */
    historySnapshotDateRaw(item) {
      if (!item) return '';
      if (item.predictDat != null && item.predictDat !== '') return item.predictDat;
      if (item.dataty === '已开票未回款' && item.predictBackDat) return item.predictBackDat;
      if (item.dataty === '已送货未开票' && item.predictVatDat) return item.predictVatDat;
      if (item.predictBackDat) return item.predictBackDat;
      if (item.predictVatDat) return item.predictVatDat;
      return '';
    },
    // 拉物料明细（由 bootstrapFeedbackData 与订单并行；失败不落盘整页，仅提示并置空列表）
    async fetchMaterialList() {
      try {
        let res = await this.$api.queryOrderTempWithItem({ vbeln: this.vbeln }, { isHideLoading: true });
        if (res.code === 200 && res.result) {
          const r = res.result;
          this.materialList = Array.isArray(r) ? r : [r];
        } else {
          this.materialList = [];
        }
      } catch (e) {
        this.materialList = [];
        this.$message.error('物料数据加载失败');
      }
    },
    // 物料折叠条点击：仅切换展开状态（物料已在 fetchMaterialList 预加载）
    toggleMaterial() {
      this.materialExpanded = !this.materialExpanded;
    },
    async urgeFollowUp() {
      try {
        let res = await this.$api.receivableUrgeFollowUp({ vbeln: this.vbeln,type: this.pageType }, { isHideLoading: true });
        if (res.code === 200) {
          this.$message.success('已发送催办通知给业务员');
        } else {
          this.$message.warning(res.message || '催办失败');
        }
      } catch (e) {
        this.$message.error('催办异常');
      }
    },
    async submitFeedback() {
      // 验证必填
      if (this.pageType == '1') {
        if (!this.changeForm.predictVatDat) {
          this.$message.warning('请选择预计开票日期');
          return;
        }
        if (!this.changeForm.vatOverdueDesc) {
          this.$message.warning('请选择未开票原因');
          return;
        }
      } else {
        if (!this.changeForm.backOverdueDesc) {
          this.$message.warning('请选择未回款原因');
          return;
        }
      }
      if (this.showRepeatOrderFields && this.changeForm.hasCustomerProof === 1 && this.changeForm.imageUrls.length === 0) {
        this.$message.warning('请上传客户证明截图');
        return;
      }
      if (!this.changeForm.feedbackRemark) {
        this.$message.warning('请填写补充说明');
        return;
      }

      try {
        let vbelns = [this.changeForm.vbeln];
        let res = await this.$api.receivableSubmitFeedback({
          ...this.changeForm,
          vbelns: vbelns,
        }, { isHideLoading: true });
        if (res.code === 200) {
          this.$message.success('反馈提交成功');
          // fetchFeedbackHistory：刷新历史记录（不传全局 Loading）
          this.fetchFeedbackHistory().catch(() => {});
          // 重置表单
          this.changeForm.feedbackRemark = '';
          this.changeForm.imageUrls = [];
          this.changeForm.repeatOrderNumber = '';
          this.changeForm.hasCustomerProof = '';
          if (this.pageType == '1') {
            this.changeForm.vatOverdueDesc = '';
            this.changeForm.predictVatDat = '';
          } else {
            this.changeForm.backOverdueDesc = '';
            this.changeForm.predictBackDat = '';
          }
        } else {
          this.$message.error(res.message || '提交失败');
        }
      } catch (e) {
        this.$message.error('提交异常');
      }
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      const isImage = /\.(jpg|jpeg|png|bmp|gif)$/i.test(file.name);
      if (!isImage) {
        this.$message.error('只能上传图片格式');
        return;
      }
      if (file.size / 1024 / 1024 > 2) {
        this.$message.error('图片大小不能超过2MB');
        return;
      }
      // 上传
      const formData = new FormData();
      formData.append('file', file);
      this.$http.post('/reqorder/upload', formData, {
        headers: { 'user_login_token': this.$store.state.token, 'Content-Type': 'multipart/form-data' }
      }).then(res => {
        if (res.code === 200) {
          this.changeForm.imageUrls.push(res.result.url);
          this.$message.success('上传成功');
        } else {
          this.$message.error(res.message || '上传失败');
        }
        event.target.value = '';
      }).catch(() => {
        this.$message.error('上传失败');
        event.target.value = '';
      });
    },
    removeImage(idx) {
      this.changeForm.imageUrls.splice(idx, 1);
    },
    previewImage(url) {
      window.open(url, '_blank');
    },
    formatAmount(val) {
      if (!val) return '0.00';
      return Number(val).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    // 格式化日期展示：yyyy-MM-dd → yyyy / MM / dd
    formatDateDisplay(val) {
      if (!val) return '';
      const parts = val.split('-');
      if (parts.length !== 3) return val;
      return `${parts[0]} / ${parts[1]} / ${parts[2]}`;
    },
    // 点击日期区域唤起原生日期选择器（与原型 showDatePicker 一致）
    showNativeDatePicker(refName) {
      const picker = this.$refs[refName];
      if (!picker) return;
      if (picker.showPicker) {
        picker.showPicker();
      } else {
        picker.click();
      }
    },
    // 原生 date 变更后写回表单字段
    handleNativeDateChange(e, field) {
      const val = e.target.value;
      if (val) {
        this.changeForm[field] = val;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.subsidiary-feedback-root {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #F5F6F7;
}

.subsidiary-feedback-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 12px;
}

.loading-wrap {
  text-align: center;
  padding: 40px;
  color: #999;
}

.card {
  background: #FFFFFF;
  margin: 12px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.card-body {
  padding: 8px 16px 16px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #111;
  border-bottom: 0.5px solid #F0F0F0;
  .icon-box {
    width: 20px; height: 20px;
    display: flex; align-items: center; justify-content: center;
    color: #999;
    svg { width: 18px; height: 18px; }
  }
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  margin-bottom: 4px;
}
.order-header-left {
  display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 0;
}
.order-header-top {
  display: flex; align-items: center; gap: 8px;
}
.order-header-bottom {
  display: flex; align-items: center;
}
.order-po-tag {
  font-size: 11px; font-weight: 600; color: #0052D9;
  background: rgba(0,82,217,0.12); padding: 2px 6px; border-radius: 4px;
}
.order-number {
  font-size: 18px; font-weight: 700; color: #111;
}
.order-link-no {
  font-size: 13px; color: #999;
}
.order-header-right {
  display: flex; flex-direction: column; align-items: flex-end; gap: 4px;
  flex-shrink: 0; margin-left: 8px;
}
.order-overdue {
  font-size: 14px; font-weight: 600; color: #FA5151;
}
.order-salesman {
  font-size: 12px; color: #999;
  .highlight { color: #576B95; font-weight: 600; }
}
.order-meta-tags {
  display: flex; gap: 8px; margin-bottom: 8px;
}
.order-meta-tag {
  font-size: 12px; color: #0052D9; background: rgba(0,82,217,0.08);
  padding: 3px 10px; border-radius: 6px; font-weight: 600;
}
.order-amount-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 6px 0;
  .info-label { font-size: 14px; color: #666; font-weight: 500; }
  .info-value { font-size: 15px; font-weight: 600; color: #111; }
  .info-value.orange { font-size: 20px; font-weight: 700; color: #FF6B35; }
  .currency { font-size: 14px; font-weight: 500; color: #FF6B35; margin-right: 1px; }
}

// 物料折叠
.material-fold-bar {
  display: flex; align-items: center; justify-content: center;
  gap: 8px; padding: 10px 0; margin-top: 4px;
  border-top: 0.5px solid #F0F0F0; cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.material-fold-text {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 500; color: #0052D9;
}
.material-fold-arrow {
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.2s; color: #999;
  svg { width: 16px; height: 16px; }
  &.expanded { transform: rotate(180deg); }
}
.material-fold-body { padding: 8px 0; }
.material-table {
  width: 100%; border-collapse: collapse;
  thead th {
    font-size: 13px; color: #999; font-weight: 500;
    text-align: left; padding: 8px 0; border-bottom: 0.5px solid #F0F0F0;
  }
  tbody td {
    font-size: 14px; color: #333; padding: 12px 0;
    border-bottom: 0.5px solid #F0F0F0;
  }
  tbody td:last-child { text-align: right; font-weight: 600; }
  tbody td.text-center { text-align: center; }
  tbody td.text-right { text-align: right; font-weight: 600; }
}
.empty-material { text-align: center; color: #999; padding: 16px; font-size: 13px; }

// 表单
.form-card { padding-top: 16px !important; }
.form-group { margin-bottom: 12px; }
// 表单项标题与「反馈历史」卡片标题一致
.form-card .form-label {
  font-size: 15px;
  font-weight: 600;
  color: #111;
  margin-bottom: 8px;
  .required { color: #FA5151; }
}
.enum-group {
  display: flex; flex-wrap: wrap; gap: 10px;
}
.enum-option {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 8px 14px; border: 1px solid #E5E5E5; border-radius: 8px;
  cursor: pointer; background: #FFFFFF; font-size: 14px; color: #333;
  min-height: 36px; gap: 6px;
  &.selected {
    border-color: #0052D9; background: rgba(0,82,217,0.06); color: #0052D9; font-weight: 600;
  }
  .check-icon {
    width: 16px; height: 16px; display: flex; align-items: center; justify-content: center;
    svg { width: 14px; height: 14px; color: #0052D9; }
  }
}

.upload-area {
  width: 100%; min-height: 80px; border: 1px dashed #E5E5E5; border-radius: 8px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; color: #B2B2B2; font-size: 14px; cursor: pointer;
  background: #F7F7F7; padding: 12px;
}
.upload-preview {
  display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px;
  .preview-item {
    position: relative;
    img { width: 60px; height: 60px; object-fit: cover; border-radius: 6px; border: 1px solid #E5E5E5; }
    .upload-remove {
      font-size: 12px; color: #FA5151; cursor: pointer; display: block; text-align: center;
    }
  }
}

// 反馈历史
.history-item { padding: 12px 0; border-bottom: 0.5px solid #F0F0F0; }
.history-item:last-child { border-bottom: none; }
.no-history { text-align: center; color: #999; font-size: 13px; padding: 16px; }
.history-header {
  display: flex; justify-content: space-between; margin-bottom: 8px;
  .history-time { font-size: 13px; color: #999; }
  .history-user { font-size: 13px; color: #07C160; font-weight: 600; }
}
.history-row { display: flex; font-size: 13px; gap: 8px; padding: 2px 0; }
.h-label { color: #999; min-width: 70px; flex-shrink: 0; }
.h-label-wide { min-width: 100px; }
.h-value { color: #666; }
.history-img { width: 40px; height: 40px; object-fit: cover; border-radius: 4px; cursor: pointer; }

// 底部按钮（文档流底部 + 安全区，避免内容被挡且减少与滚动层触摸冲突）
.bottom-action-bar {
  flex-shrink: 0;
  align-self: center;
  width: 100%;
  max-width: 750px;
  box-sizing: border-box;
  background: #FFFFFF;
  border-top: 0.5px solid #E5E5E5;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -2px 8px rgba(0,0,0,0.04);
}
.action-btn-secondary {
  flex: 1;
  height: 44px;
  background: #F7F7F7;
  color: #333;
  border: 1px solid #E5E5E5;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  display: flex; align-items: center; justify-content: center; gap: 6px;
}
// 日期选择：只读展示 + 透明原生 date 层（无图标、不弹键盘）
.date-input-wrapper {
  position: relative;
  width: 100%;
}
.form-date-display {
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  border: 1px solid #E5E5E5;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #333;
  background: #fff;
  outline: none;
  -webkit-appearance: none;
  pointer-events: none;
  &::placeholder {
    color: #B2B2B2;
  }
}
.date-native-overlay {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  border: none;
  margin: 0;
  padding: 0;
}
// 重复订单等单行控件：统一 44px 高度
.form-control-field {
  width: 100%;
  /deep/ .el-input__inner {
    height: 44px;
    line-height: 44px;
    font-size: 14px;
    border-radius: 8px;
    padding: 0 12px;
  }
}
// 补充说明多行框：正文字号与 placeholder 与重复订单单行框一致（14px）
.form-remark-field {
  width: 100%;
  /deep/ .el-textarea__inner {
    font-size: 14px;
    line-height: 1.5;
    border-radius: 8px;
    padding: 10px 12px;
    &::placeholder {
      font-size: 14px;
    }
  }
}

.action-btn-primary {
  flex: 1.5;
  height: 44px;
  background: #0052D9 !important;
  color: #fff !important;
  border: none !important;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 82, 217, 0.25);
  display: flex; align-items: center; justify-content: center; gap: 6px;
  /deep/ span {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #fff;
  }
  svg {
    stroke: #fff;
  }
  &:active {
    background: #0043B5 !important;
  }
}
</style>
